import React, { useState, useRef, useEffect } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import toast from 'react-hot-toast';
import axios from 'axios';
import { getApiUrl } from "@/lib/apiUrl";

interface FormData {
  name: string;
  message: string;
  email: string; // Honeypot field
  'g-recaptcha-response'?: string;
}

interface Comment {
  id: number;
  name: string;
  message: string;
  comment: string;
  date: string;
  likes: number;
  dislikes: number;
  liked: boolean;
  disliked: boolean;
  parent_id?: number | null;
  replies: Comment[];
}

interface CommentSectionProps {
  id: number;
}

const CommentSectionView: React.FC<CommentSectionProps> = ({ id }) => {
  const blogId = id;
  const [formData, setFormData] = useState<FormData>({
    name: '',
    message: '',
    email: '',
  });
  const [errors, setErrors] = useState<Partial<FormData & { captcha: string }>>({});
  const [formLoading, setFormLoading] = useState(false);
  const [showForm, setShowForm] = useState(true);
  const [comments, setComments] = useState<Comment[]>([]);
  const [replyToCommentId, setReplyToCommentId] = useState<number | null>(null);
  const [showRepliesList, setShowRepliesList] = useState<number[]>([]);
  const [recaptchaToken, setRecaptchaToken] = useState<string | null>(null);
  const recaptchaRef = useRef<ReCAPTCHA>(null);
  const formRef = useRef<HTMLFormElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const commentRefs = useRef<{ [key: number]: HTMLDivElement | null }>({});

  const recaptchaSiteKey = '6Lf9iEYqAAAAAGBkHMfKHyrXoDP3tyX_yZ8cL9b-';

  // Color palette for dynamic avatar colors
  const colorPalette = [
    { bg: '#FADADD', text: '#C0392B' }, // Pastel Red
    { bg: '#D4EFDF', text: '#27AE60' }, // Pastel Green
    { bg: '#D6EAF8', text: '#2980B9' }, // Pastel Blue
    { bg: '#F5E1FD', text: '#8E44AD' }, // Pastel Purple
    { bg: '#FDE3CF', text: '#D35400' }, // Pastel Orange
  ];

  // Hash function to generate consistent color based on name
  const getColorForName = (name: string) => {
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    const index = Math.abs(hash) % colorPalette.length;
    return colorPalette[index];
  };

  const validateForm = (isReply: boolean = false): boolean => {
    const newErrors: Partial<FormData & { captcha: string }> = {};
    if (!formData.name.trim() || formData.name.length < 2) {
      newErrors.name = 'Name must be at least 2 characters long';
    }
    if (!formData.message.trim() || formData.message.length < 5) {
      newErrors.message = 'Message must be at least 5 characters long';
    }
    if (formData.email.trim()) {
      return false; // Honeypot triggered
    }
    if (!isReply && !recaptchaToken) {
      newErrors.captcha = 'Please complete the reCAPTCHA';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const fetchComments = async () => {
    try {
      const response = await axios.post(
        getApiUrl('/blog/comment/fetch'),
        { id: blogId },
        { headers: { 'Content-Type': 'application/json' } }
      );
      const fetchedComments = response.data.data.comments.map((comment: any) => ({
        id: comment.id,
        name: comment.name,
        message: comment.comment,
        comment: comment.comment,
        date: new Date(comment.created_at).toLocaleDateString('en-GB', {
          day: '2-digit',
          month: 'short',
          year: 'numeric',
        }),
        likes: comment.lens || 0,
        dislikes: comment.dislikes || 0,
        liked: false,
        disliked: false,
        parent_id: comment.parent_id || null,
        replies: comment.replies || [],
      }));
      setComments(fetchedComments);
    } catch (error) {
      console.error('Error fetching comments:', error);
      notifyerror('Failed to fetch comments.');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm(!!replyToCommentId)) return;

    setFormLoading(true);
    setShowForm(false);

    try {
      const payload = {
        blog_id: blogId,
        name: formData.name,
        comment: formData.message,
        'g-recaptcha-response': recaptchaToken,
        ...(replyToCommentId && { parent_id: replyToCommentId }),
      };

      const response = await axios.post(
        getApiUrl('/blog/comment'),
        payload,
        { headers: { 'Content-Type': 'application/json' } }
      );

      if (response.status) {
        await fetchComments();
        notify();
        setFormData({ name: '', message: '', email: '' });
        setRecaptchaToken(null);
        recaptchaRef.current?.reset();
        setErrors({});
        setReplyToCommentId(null);

        // Scroll to the reply if it was a reply submission
        if (response.data.data && response.data.data.id) {
          setTimeout(() => {
            const newCommentId = response.data.data.id;
            if (commentRefs.current[newCommentId]) {
              commentRefs.current[newCommentId]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
          }, 100); // Small delay to ensure DOM updates
        }
      } else {
        notifyerror('Comment submission failed.');
      }
    } catch (error: any) {
      console.error('Error submitting comment:', error);
      notifyerror('Failed to submit comment.');
    } finally {
      setFormLoading(false);
      setShowForm(true);
    }
  };

  const notify = () =>
    toast.success('Comment submitted successfully.', {
      style: {
        border: '1px solid #00b852',
        padding: '16px',
        color: '#00b852',
        zIndex: 10000,
      },
      iconTheme: {
        primary: '#00b852',
        secondary: '#FFFFFF',
      },
    });

  const notifyerror = (msg: string) =>
    toast.error(msg, {
      style: {
        border: '1px solid #cc0000',
        padding: '16px',
        color: '#cc0000',
        zIndex: 10000,
      },
      iconTheme: {
        primary: '#cc0000',
        secondary: '#FFFFFF',
      },
    });

  const handleLike = (commentId: number) => {
    setComments(
      comments.map((comment) =>
        comment.id === commentId
          ? {
            ...comment,
            likes: comment.liked ? comment.likes - 1 : comment.likes + 1,
            liked: !comment.liked,
            dislikes: comment.disliked ? comment.dislikes - 1 : comment.dislikes,
            disliked: false,
          }
          : comment
      )
    );
  };

  const handleDislike = (commentId: number) => {
    setComments(
      comments.map((comment) =>
        comment.id === commentId
          ? {
            ...comment,
            dislikes: comment.disliked ? comment.dislikes - 1 : comment.dislikes + 1,
            disliked: !comment.disliked,
            likes: comment.liked ? comment.likes - 1 : comment.likes,
            liked: false,
          }
          : comment
      )
    );
  };

  const handleReplyClick = (commentId: number) => {
    setReplyToCommentId(commentId);
    formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    textareaRef.current?.focus();
  };

  const toggleRepliesList = (commentId: number) => {
    setShowRepliesList((prev) =>
      prev.includes(commentId) ? prev.filter((id) => id !== commentId) : [...prev, commentId]
    );
  };

  useEffect(() => {
    fetchComments();
  }, []);

  return (
    <section className="md:container mb-10">
      {showForm && !formLoading ? (
        <form
          ref={formRef}
          onSubmit={handleSubmit}
          className="border-t md:container border-b border-[#E4E9F0] py-4 my-4"
        >
          <label
            htmlFor="message"
            className="text-[#090D28] mb-1 text-sm font-normal leading-none font-LatinkaRegular"
          >
            {replyToCommentId
              ? `Replying to ${comments.find((c) => c.id === replyToCommentId)?.name || 'Comment'
              }`
              : 'Add Comment'}
          </label>
          <textarea
            ref={textareaRef}
            id="message"
            name="message"
            placeholder="Write here..."
            className="flex mb-3.5 w-full md:w-[500px] h-[100px] p-3 items-start gap-2.5 self-stretch rounded-[8px] bg-[#F1F2F6] placeholder:text-[#8394A6] placeholder:text-[15px] placeholder:font-normal placeholder:leading-none font-LatinkaRegular"
            value={formData.message}
            onChange={(e) => setFormData({ ...formData, message: e.target.value })}
          />
          {errors.message && <p className="text-red-500 text-xs mb-2">{errors.message}</p>}

          <label
            htmlFor="name"
            className="text-[#090D28] mb-1 text-sm font-normal leading-none font-LatinkaRegular"
          >
            Name
          </label>
          <input
            type="text"
            id="name"
            name="name"
            className="flex h-10 p-3 w-full md:w-[500px] items-start gap-2.5 self-stretch rounded-[8px] bg-[#F1F2F6] placeholder:text-[#8394A6] placeholder:text-[15px] placeholder:font-normal placeholder:leading-none font-LatinkaRegular"
            placeholder="Enter a name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          />
          {errors.name && <p className="text-red-500 text-xs mb-2">{errors.name}</p>}

          {/* Honeypot Field */}
          <input
            type="text"
            name="email"
            className="hidden"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
          />

          <div className="flex justify-center flex-col items-center w-full md:w-[500px] my-3.5">
            <ReCAPTCHA
              ref={recaptchaRef}
              sitekey={recaptchaSiteKey}
              onChange={(token) => {
                setRecaptchaToken(token);
                setErrors((prev) => ({ ...prev, captcha: '' }));
              }}
              onExpired={() => {
                setRecaptchaToken(null);
                notifyerror('reCAPTCHA expired. Please try again.');
              }}
            />
            {errors.captcha && <p className="text-red-500 text-xs mt-2">{errors.captcha}</p>}
          </div>

          <p className="text-[#56606B] text-xs font-normal leading-[120%] font-LatinkaRegular my-3.5">
            Email won’t be published. Comments appear after review.
          </p>
          <div className="flex gap-2">
            <button
              type="submit"
              disabled={formLoading}
              className="flex w-[240px] h-10 px-5 py-4 justify-center items-center rounded-[8px] bg-[#195AFF] text-white text-base font-normal leading-[120%] font-LatinkaSemiBold disabled:opacity-50"
            >
              Submit {replyToCommentId ? 'Reply' : 'Comment'}
            </button>
            {replyToCommentId && (
              <button
                type="button"
                onClick={() => setReplyToCommentId(null)}
                className="flex w-[100px] h-10 px-5 py-4 justify-center items-center rounded-[8px] bg-[#888F97] text-white text-base font-normal leading-[120%] font-LatinkaSemiBold"
              >
                Cancel
              </button>
            )}
          </div>
        </form>
      ) : (
        <div className="border-t md:container border-b border-[#E4E9F0] py-4 my-4 text-center text-[#090D28] text-base font-normal leading-none font-LatinkaMedium">
          <div className="w-full text-center pb-16 text-gray-500 text-lg font-LatinkaMedium">
            <div className="flex justify-center items-center h-40">
              <svg className="animate-spin h-10 w-10 text-[#195AFF]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="#195AFF" strokeWidth="4"></circle>
                <path className="opacity-75" fill="#195AFF" d="M4 12a8 8 0 018-8v8z"></path>
              </svg>
            </div>
            Loading ...
          </div>
        </div>
      )}

      {comments.length > 0 && (
        <div className="mt-6">
          <p className="text-[#090D28] mb-[16px] flex items-center text-xl font-normal leading-none font-LatinkaBold">
            Comments{' '}
            <span className="flex flex-col w-5 h-5 p-1 justify-center items-center gap-2.5 rounded bg-[#195AFF] text-white text-sm font-normal leading-none font-LatinkaRegular ml-2">
              {comments.length}
            </span>
          </p>

          {comments.map((comment) => {
            const { bg, text } = getColorForName(comment.name);
            const isRepliesVisible = showRepliesList.includes(comment.id);
            return (
              <div
                key={comment.id}
                className="mt-[40px] w-full"
                ref={(el) => {
                  commentRefs.current[comment.id] = el;
                }}

              >
                <div className="flex items-start justify-start w-full gap-[8px]">
                  <div className="w-[40px]">
                    <div
                      className="text-base font-normal leading-none font-LatinkaSemiBold h-[30px] w-[30px] flex justify-center items-center rounded-full"
                      style={{ backgroundColor: bg, color: text }}
                    >
                      {comment.name.charAt(0).toUpperCase()}
                    </div>
                  </div>
                  <div className="w-full">
                    <div className="flex mb-[8px] items-center gap-[8px]">
                      <p className="text-[#2E2F3B] text-sm font-normal leading-none font-LatinkaSemiBold">
                        {comment.name}
                      </p>
                      <p className="text-[#888F97] text-xs font-normal leading-[120%] font-LatinkaRegular">
                        {comment.date}
                      </p>
                    </div>
                    <p className="text-[#090D28] mb-[8px] text-base font-normal leading-none font-LatinkaMedium mt-2">
                      {comment.comment}
                    </p>
                    <div className="flex gap-4 mt-2">
                      <div className="flex items-center gap-1">
                        <svg
                          className="cursor-pointer"
                          onClick={() => handleLike(comment.id)}
                          xmlns="http://www.w3.org/2000/svg"
                          width="20"
                          height="21"
                          viewBox="0 0 20 21"
                          fill="none"
                        >
                          <path
                            d="M18.2194 9.35758C18.127 8.98283 17.9535 8.63291 17.711 8.3326C17.4689 8.03305 17.1609 7.79347 16.811 7.63258C16.4623 7.46654 16.0806 7.38111 15.6944 7.38258H12.0027L12.2194 6.54925C12.4204 5.75607 12.3439 4.91795 12.0027 4.17424C11.6548 3.43375 11.0677 2.83198 10.336 2.46593L9.41936 2.01592C9.22761 1.92113 9.01653 1.87183 8.80269 1.87183C8.58878 1.87183 8.37778 1.92113 8.186 2.01592C7.99444 2.11498 7.82888 2.25771 7.70267 2.43258C7.57479 2.60387 7.49172 2.80439 7.461 3.01593L6.67767 7.72425C6.55093 7.83997 6.43644 7.96842 6.336 8.10758C6.14584 7.87743 5.90808 7.69122 5.63906 7.56175C5.37004 7.43228 5.07618 7.36263 4.77767 7.35758H3.79434C3.23739 7.35758 2.70324 7.57884 2.30941 7.97267C1.91559 8.36649 1.69434 8.90066 1.69434 9.45758V16.3659C1.69434 16.9229 1.91559 17.457 2.30941 17.8508C2.70324 18.2447 3.23739 18.4659 3.79434 18.4659H4.77767C5.0865 18.4672 5.39177 18.3999 5.67137 18.2687C5.95098 18.1376 6.19792 17.9459 6.39434 17.7076C6.88375 18.194 7.54598 18.4667 8.236 18.4659H14.5027C15.1083 18.4742 15.6977 18.2708 16.1694 17.8909C16.6464 17.5112 16.9716 16.9732 17.086 16.3742L18.2694 10.4659C18.3316 10.0972 18.3145 9.71924 18.2194 9.35758ZM5.611 16.3992C5.611 16.6202 5.5232 16.8322 5.36693 16.9885C5.21064 17.1447 4.99869 17.2326 4.77767 17.2326H3.79434C3.57332 17.2326 3.36136 17.1447 3.20508 16.9885C3.0488 16.8322 2.961 16.6202 2.961 16.3992V9.49091C2.961 9.26991 3.0488 9.05799 3.20508 8.90166C3.36136 8.74541 3.57332 8.65758 3.79434 8.65758H4.77767C4.99869 8.65758 5.21064 8.74541 5.36693 8.90166C5.5232 9.05799 5.611 9.26991 5.611 9.49091V16.3992Z"
                            fill={comment.liked ? '#195AFF' : '#888F97'}
                          />
                        </svg>
                        <span className="text-xs">{comment.likes}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <svg
                          onClick={() => handleDislike(comment.id)}
                          className="cursor-pointer"
                          xmlns="http://www.w3.org/2000/svg"
                          width="20"
                          height="21"
                          viewBox="0 0 20 21"
                          fill="none"
                        >
                          <path
                            d="M18.2409 9.83558L17.0575 3.92723C16.9408 3.32691 16.6163 2.78694 16.1409 2.40223C15.6658 2.02954 15.0779 1.8297 14.4742 1.83558H8.24087C7.89905 1.83428 7.56033 1.90062 7.24427 2.03076C6.92819 2.16091 6.64104 2.35231 6.39924 2.59391C6.20449 2.35578 5.95899 2.16412 5.68075 2.03293C5.4025 1.90173 5.09848 1.83431 4.79085 1.83558H3.79924C3.24229 1.83558 2.70808 2.05683 2.31425 2.45066C1.92043 2.84448 1.69922 3.37861 1.69922 3.93557V10.8439C1.69922 11.4008 1.92043 11.935 2.31425 12.3288C2.70808 12.7227 3.24229 12.9439 3.79924 12.9439H4.79919C5.08977 12.9447 5.37726 12.8844 5.64291 12.7667C5.90855 12.6489 6.14635 12.4764 6.34084 12.2606C6.44128 12.3997 6.55579 12.5282 6.68253 12.6439L7.46583 17.3523C7.50031 17.5639 7.58388 17.7646 7.70976 17.9382C7.83564 18.1118 8.00036 18.2537 8.19082 18.3523C8.38285 18.4469 8.59352 18.4981 8.8076 18.5022C9.02193 18.5001 9.23285 18.4488 9.42427 18.3523L10.3409 17.9023C11.0765 17.5397 11.6651 16.9352 12.0079 16.1903C12.3509 15.4453 12.4273 14.6052 12.2242 13.8106L12.0075 12.9773H15.7075C16.0913 12.9801 16.4705 12.8945 16.8159 12.7273C17.3398 12.4771 17.7652 12.0592 18.0248 11.54C18.2844 11.0208 18.3634 10.4298 18.2492 9.86058L18.2409 9.83558ZM5.63253 10.8439C5.63253 11.0649 5.54477 11.2768 5.38849 11.4332C5.23221 11.5894 5.02021 11.6772 4.79919 11.6772H3.80758C3.58657 11.6772 3.37457 11.5894 3.21829 11.4332C3.062 11.2768 2.97424 11.0649 2.97424 10.8439V3.93557C2.97424 3.71455 3.062 3.5026 3.21829 3.34633C3.37457 3.19004 3.58657 3.10223 3.80758 3.10223H4.79919C5.02021 3.10223 5.23221 3.19004 5.38849 3.34633C5.54477 3.5026 5.63253 3.71455 5.63253 3.93557V10.8439Z"
                            fill={comment.disliked ? '#195AFF' : '#888F97'}
                          />
                        </svg>
                        <span className="text-xs">{comment.dislikes}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <svg
                          onClick={() => handleReplyClick(comment.id)}
                          className="cursor-pointer"
                          xmlns="http://www.w3.org/2000/svg"
                          width="20"
                          height="21"
                          viewBox="0 0 20 21"
                          fill="none"
                        >
                          <path
                            d="M18.3708 6.64346L18.3291 11.8939C18.3302 12.4116 18.2283 12.9244 18.0291 13.4022C17.8327 13.8791 17.5438 14.3124 17.1791 14.6771C16.8144 15.0419 16.3811 15.3308 15.9041 15.5272C15.4263 15.7264 14.9136 15.8284 14.3958 15.8273H12.4541L10.7875 17.4939C10.6843 17.6022 10.5593 17.6874 10.4208 17.7439C10.2769 17.8095 10.1206 17.8436 9.96247 17.8439C9.80522 17.8433 9.64955 17.8121 9.50413 17.7522C9.35888 17.6879 9.22838 17.5942 9.1208 17.4772L7.49581 15.8605H5.55418C5.03803 15.8605 4.52697 15.7588 4.05022 15.561C3.57348 15.3632 3.14038 15.0734 2.7758 14.708C2.41123 14.3426 2.12229 13.9089 1.92553 13.4318C1.72878 12.9546 1.62806 12.4434 1.62916 11.9272V6.42721C1.62817 5.91041 1.72925 5.39851 1.9266 4.92086C2.12395 4.44323 2.41368 4.00926 2.77916 3.64388C3.51864 2.9131 4.51454 2.50041 5.55418 2.49391H14.4375C14.9543 2.49281 15.4663 2.59379 15.944 2.79107C16.4217 2.98835 16.8557 3.27804 17.2212 3.64351C17.5866 4.00896 17.8764 4.44301 18.0736 4.92071C18.271 5.39842 18.3719 5.91037 18.3708 6.42721V6.64346Z"
                            fill="#888F97"
                          />
                        </svg>
                        <span className="text-xs">{comment.replies.length}</span>
                      </div>
                    </div>
                    {comment.replies.length > 0 && (
                      <button
                        onClick={() => toggleRepliesList(comment.id)}
                        className="my-[24px] text-[#888F97] text-sm border-none outline-none focus:outline-none font-normal leading-[120%] font-LatinkaRegular flex items-center gap-[8px]"
                      >
                        Replies
                        {isRepliesVisible ? (
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="16"
                            height="17"
                            viewBox="0 0 16 17"
                            fill="none"
                          >
                            <path
                              d="M13.3334 10.7197L8.94235 6.32852C8.69121 6.08102 8.35275 5.94238 8.00008 5.94238C7.64741 5.94238 7.30895 6.08102 7.05782 6.32852L2.66675 10.7197"
                              stroke="black"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        ) : (
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="16"
                            height="17"
                            viewBox="0 0 16 17"
                            fill="none"
                          >
                            <path
                              d="M2.66675 6.28027L7.05782 10.6715C7.30895 10.919 7.64741 11.0577 8.00008 11.0577C8.35275 11.0577 8.69121 10.919 8.94235 10.6715L13.3334 6.28027"
                              stroke="black"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        )}
                      </button>
                    )}
                    {isRepliesVisible && (
                      <div>
                        {comment.replies.map((reply) => {
                          const { bg: replyBg, text: replyText } = getColorForName(reply.name);
                          return (
                            <div
                              key={reply.id}
                              className="mt-4 ml-8"
                             ref={(el) =>  {(commentRefs.current[reply.id] = el)}}
                            >
                              <div className="flex items-start gap-4 mb-3">
                                <div
                                  className="text-base font-normal leading-none font-LatinkaSemiBold h-[30px] w-[30px] flex justify-center items-center rounded-full"
                                  style={{ backgroundColor: replyBg, color: replyText }}
                                >
                                  {reply.name.charAt(0).toUpperCase()}
                                </div>
                                <div>
                                  <div className="flex mt-1 items-center gap-4">
                                    <p className="text-[#2E2F3B] text-sm font-normal leading-none font-LatinkaSemiBold">
                                      {reply.name}
                                    </p>
                                    <p className="text-[#888F97] text-xs font-normal leading-[120%] font-LatinkaRegular">
                                      {reply.date}
                                    </p>
                                  </div>
                                  <p className="text-[#090D28] text-base font-normal leading-none font-LatinkaMedium mt-2">
                                    {reply.comment}
                                  </p>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
};

export default CommentSectionView;